/**
 * Provider Profile Settings Page
 *
 * Allows providers to upload logo/banner and edit their public profile information.
 * Changes are saved to the API and reflected on their public profile page.
 */

/* eslint-disable max-lines */

import type { ApiUpdateProviderProfileRequest } from "@lumi/data";
import { authenticatedFetch } from "@lumi/data";
import { Check, ExternalLink, Loader2, Save, X } from "lucide-react";
import { useCallback, useEffect, useState, type ReactElement } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../../auth/AuthContext";
import { createRefreshCallback } from "../../../auth/refreshCallback";
import { LoadingSpinner } from "../../../shared/components/loading/LoadingSpinner";
import { Button } from "../../../shared/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../../shared/components/ui/card";
import { Input } from "../../../shared/components/ui/input";
import { Label } from "../../../shared/components/ui/label";
import { Textarea } from "../../../shared/components/ui/textarea";
import { ProfileImageUpload } from "../components/ProfileImageUpload";

interface ProviderProfile {
  id: string;
  userId: string;
  slug: string;
  displayName: string;
  bio: string | null;
  logoUrl: string | null;
  logoSqUrl: string | null;
  bannerUrl: string | null;
  websiteUrl: string | null;
  socialLinks: {
    twitter?: string;
    instagram?: string;
    youtube?: string;
    vimeo?: string;
  } | null;
  verified: boolean;
  titleCount: number;
}

interface ProfileResponse {
  profile: ProviderProfile;
}

/**
 * Hook to fetch and manage provider profile state
 */
function useProviderProfile() {
  const { accessToken, refreshAuth } = useAuth();
  const [profile, setProfile] = useState<ProviderProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchProfile = useCallback(async () => {
    if (!accessToken) return;

    try {
      setLoading(true);
      setError(null);
      const response = await authenticatedFetch<ProfileResponse>(
        `${import.meta.env.VITE_API_BASE_URL}/v1/provider/profile`,
        { method: "GET" },
        accessToken,
        createRefreshCallback(refreshAuth),
      );
      setProfile(response.profile);
    } catch (err) {
      console.error("Failed to fetch profile:", err);
      setError(err instanceof Error ? err.message : "Failed to load profile");
    } finally {
      setLoading(false);
    }
  }, [accessToken, refreshAuth]);

  useEffect(() => {
    void fetchProfile();
  }, [fetchProfile]);

  return { profile, setProfile, loading, error, refetch: fetchProfile };
}

/**
 * Provider Profile Settings Page
 */
// eslint-disable-next-line max-lines-per-function
export function ProviderProfileSettings(): ReactElement {
  const { accessToken, refreshAuth } = useAuth();
  const { profile, setProfile, loading, error } = useProviderProfile();

  // Form state
  const [displayName, setDisplayName] = useState("");
  const [bio, setBio] = useState("");
  const [websiteUrl, setWebsiteUrl] = useState("");
  const [twitterUrl, setTwitterUrl] = useState("");
  const [instagramUrl, setInstagramUrl] = useState("");
  const [youtubeUrl, setYoutubeUrl] = useState("");
  const [vimeoUrl, setVimeoUrl] = useState("");

  // UI state
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  // Sync form state with profile
  useEffect(() => {
    if (profile) {
      setDisplayName(profile.displayName);
      setBio(profile.bio ?? "");
      setWebsiteUrl(profile.websiteUrl ?? "");
      setTwitterUrl(profile.socialLinks?.twitter ?? "");
      setInstagramUrl(profile.socialLinks?.instagram ?? "");
      setYoutubeUrl(profile.socialLinks?.youtube ?? "");
      setVimeoUrl(profile.socialLinks?.vimeo ?? "");
    }
  }, [profile]);

  // Track changes
  useEffect(() => {
    if (!profile) return;
    const changed =
      displayName !== profile.displayName ||
      bio !== (profile.bio ?? "") ||
      websiteUrl !== (profile.websiteUrl ?? "") ||
      twitterUrl !== (profile.socialLinks?.twitter ?? "") ||
      instagramUrl !== (profile.socialLinks?.instagram ?? "") ||
      youtubeUrl !== (profile.socialLinks?.youtube ?? "") ||
      vimeoUrl !== (profile.socialLinks?.vimeo ?? "");
    setHasChanges(changed);
  }, [profile, displayName, bio, websiteUrl, twitterUrl, instagramUrl, youtubeUrl, vimeoUrl]);

  const handleSave = useCallback(async () => {
    if (!accessToken || !profile) return;

    setSaving(true);
    setSaveError(null);
    setSaveSuccess(false);

    try {
      const updateData: ApiUpdateProviderProfileRequest = {
        displayName,
        bio: bio || undefined,
        websiteUrl: websiteUrl || undefined,
        socialLinks: {
          ...(twitterUrl ? { twitter: twitterUrl } : {}),
          ...(instagramUrl ? { instagram: instagramUrl } : {}),
          ...(youtubeUrl ? { youtube: youtubeUrl } : {}),
          ...(vimeoUrl ? { vimeo: vimeoUrl } : {}),
        },
      };

      const response = await authenticatedFetch<ProfileResponse>(
        `${import.meta.env.VITE_API_BASE_URL}/v1/provider/profile`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(updateData),
        },
        accessToken,
        createRefreshCallback(refreshAuth),
      );

      setProfile(response.profile);
      setSaveSuccess(true);
      setHasChanges(false);

      // Clear success message after 3 seconds
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      console.error("Failed to save profile:", err);
      setSaveError(err instanceof Error ? err.message : "Failed to save profile");
    } finally {
      setSaving(false);
    }
  }, [
    accessToken,
    refreshAuth,
    profile,
    displayName,
    bio,
    websiteUrl,
    twitterUrl,
    instagramUrl,
    youtubeUrl,
    vimeoUrl,
    setProfile,
  ]);

  const handleLogoUpload = useCallback(
    async (r2Key: string) => {
      if (!accessToken || !profile) return;

      try {
        const response = await authenticatedFetch<ProfileResponse>(
          `${import.meta.env.VITE_API_BASE_URL}/v1/provider/profile`,
          {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ logoUrl: r2Key }),
          },
          accessToken,
          createRefreshCallback(refreshAuth),
        );
        setProfile(response.profile);
      } catch (err) {
        console.error("Failed to update logo URL:", err);
      }
    },
    [accessToken, refreshAuth, profile, setProfile],
  );

  const handleLogoSqUpload = useCallback(
    async (r2Key: string) => {
      if (!accessToken || !profile) return;

      try {
        const response = await authenticatedFetch<ProfileResponse>(
          `${import.meta.env.VITE_API_BASE_URL}/v1/provider/profile`,
          {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ logoSqUrl: r2Key }),
          },
          accessToken,
          createRefreshCallback(refreshAuth),
        );
        setProfile(response.profile);
      } catch (err) {
        console.error("Failed to update square logo URL:", err);
      }
    },
    [accessToken, refreshAuth, profile, setProfile],
  );

  const handleBannerUpload = useCallback(
    async (r2Key: string) => {
      if (!accessToken || !profile) return;

      try {
        const response = await authenticatedFetch<ProfileResponse>(
          `${import.meta.env.VITE_API_BASE_URL}/v1/provider/profile`,
          {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ bannerUrl: r2Key }),
          },
          accessToken,
          createRefreshCallback(refreshAuth),
        );
        setProfile(response.profile);
      } catch (err) {
        console.error("Failed to update banner URL:", err);
      }
    },
    [accessToken, refreshAuth, profile, setProfile],
  );

  if (loading) {
    return (
      <div className="p-8">
        <LoadingSpinner centered />
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div className="p-8">
        <div className="text-center text-red-400">
          <p>{error || "Failed to load profile"}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 py-8">
      {/* Header */}
      <div className="space-y-2">
        <h1 className="text-2xl font-bold text-white mb-2">Profile Settings</h1>
        <p className="text-sm text-neutral-400 pb-6">
          Customize your public profile. Changes will be visible on your{" "}
          <Link
            to={`/home/providers/${profile.slug}`}
            viewTransition
            className="text-lumi-accent hover:underline inline-flex items-center gap-1"
          >
            public profile page
            <ExternalLink className="w-3 h-3" />
          </Link>
        </p>
      </div>

      <div className="grid grid-cols-5">
        {/* Banner Upload */}
        <Card className="bg-transparent border-none col-span-2">
          <CardHeader>
            <CardTitle className="text-base">Banner Image</CardTitle>
            <CardDescription className="text-xs">16:9 recommended (1920×1080px)</CardDescription>
          </CardHeader>
          <CardContent>
            <ProfileImageUpload
              currentUrl={profile.bannerUrl}
              imageType="banner"
              onUploadComplete={(url) => void handleBannerUpload(url)}
            />
          </CardContent>
        </Card>

        {/* Logo Upload */}
        <Card className="bg-transparent border-none col-span-2">
          <CardHeader>
            <CardTitle className="text-base">Logo</CardTitle>
            <CardDescription className="text-xs">Transparent Background Required (1208×330px)</CardDescription>
          </CardHeader>
          <CardContent>
            <ProfileImageUpload
              currentUrl={profile.logoUrl}
              imageType="logo"
              onUploadComplete={(url) => void handleLogoUpload(url)}
            />
          </CardContent>
        </Card>

        {/* Square Logo for Featured Rails */}
        <Card className="bg-transparent border-none col-span-1">
          <CardHeader>
            <CardTitle className="text-base">Square Logo</CardTitle>
            <CardDescription className="text-xs">1:1 (512×512px)</CardDescription>
          </CardHeader>
          <CardContent>
            <ProfileImageUpload
              currentUrl={profile.logoSqUrl}
              imageType="logo-square"
              onUploadComplete={(url) => void handleLogoSqUpload(url)}
            />
          </CardContent>
        </Card>
      </div>

      {/* Profile Form */}
      <div className="">
        <Card className="bg-transparent border-none">
          <CardHeader>
            <CardTitle className="text-base">Profile Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-2 gap-6">
              {/* Display Name */}
              <div className="space-y-2 col-span-1">
                <Label htmlFor="displayName">Display Name</Label>
                <Input
                  id="displayName"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  placeholder="Your display name"
                />
              </div>

              {/* Website */}
              <div className="space-y-2 col-span-1">
                <Label htmlFor="websiteUrl">Website</Label>
                <Input
                  type="url"
                  id="websiteUrl"
                  value={websiteUrl}
                  onChange={(e) => setWebsiteUrl(e.target.value)}
                  placeholder="https://your-website.com"
                />
              </div>

              {/* Bio */}
              <div className="space-y-2 col-span-2">
                <Label htmlFor="bio">Bio</Label>
                <Textarea
                  id="bio"
                  value={bio}
                  onChange={(e) => setBio(e.target.value)}
                  rows={8}
                  placeholder="Tell viewers about yourself..."
                />
                <p className="text-xs text-neutral-500">{bio.length}/2000 characters</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Social Links */}
      <Card className="bg-transparent border-none">
        <CardHeader>
          <CardTitle className="text-base">Social Links</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="twitter">Twitter / X</Label>
              <Input
                type="url"
                id="twitter"
                value={twitterUrl}
                onChange={(e) => setTwitterUrl(e.target.value)}
                placeholder="https://twitter.com/username"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="instagram">Instagram</Label>
              <Input
                type="url"
                id="instagram"
                value={instagramUrl}
                onChange={(e) => setInstagramUrl(e.target.value)}
                placeholder="https://instagram.com/username"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="youtube">YouTube</Label>
              <Input
                type="url"
                id="youtube"
                value={youtubeUrl}
                onChange={(e) => setYoutubeUrl(e.target.value)}
                placeholder="https://youtube.com/@channel"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="vimeo">Vimeo</Label>
              <Input
                type="url"
                id="vimeo"
                value={vimeoUrl}
                onChange={(e) => setVimeoUrl(e.target.value)}
                placeholder="https://vimeo.com/username"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Save Button */}
      <div className="flex items-center gap-4">
        <Button onClick={() => void handleSave()} disabled={saving || !hasChanges} size="lg">
          {saving ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
              Saving...
            </>
          ) : (
            <>
              <Save className="w-4 h-4 mr-2" />
              Save Changes
            </>
          )}
        </Button>

        {saveSuccess && (
          <span className="flex items-center gap-1 text-green-400 text-sm">
            <Check className="w-4 h-4" />
            Saved successfully
          </span>
        )}

        {saveError && (
          <span className="flex items-center gap-1 text-red-400 text-sm">
            <X className="w-4 h-4" />
            {saveError}
          </span>
        )}
      </div>
    </div>
  );
}
